import { useEffect, useMemo, useRef, useState } from "react";
import { T, UI, FONT } from "../constants/tokens";
import { TYPE } from "../constants/typography";
import { ACADEMY_ENABLED } from "../constants/features";
import { fmtPct } from "../utils/formatters";
import { daysUntilText } from "../utils/homeChanges";
import { fetchEarningsCalendar } from "../utils/earningsCalendar";
import { openAcademyLesson, isLessonShown } from "../utils/academyNav";
import { fetchIntelClusters, fetchIntelSeen, markIntelSeen } from "../utils/intelData";
import { rankFor, seenRows, nextEventFor, holdingWeights, holdingStats, moveSince, dayOf } from "../utils/intelRank";
import { fetchSecFilings, fetchCompanyAbout, fetchInsiderTrades, fetchCompanyNames } from "../utils/secFilings";
import { fetchCloses } from "../utils/marketdata";
import { shownDir } from "../utils/formatters";
import { openTopics } from "../utils/intelTopics";
import { useIsDesktop } from "../utils/hooks";
import { Bidi } from "./MarketTodayCard";
import GeoIntelTab from "./GeoIntelTab";
import MarketPicture from "./MarketPicture";
import IntelInsiders from "./IntelInsiders";
import InfoTip from "./InfoTip";
import EmptyState from "./EmptyState";
import LTR from "./LTR";
import { Skeleton as Skel, Loading } from "./Skeleton";

// ─── מודיעין שוק (sprint 20, stage 2) ───────────────────────────────────────
// "בהתחשב במה שאתה מחזיק, מה קרה שחשוב לך להבין?" (intel-spec.md)
//
// Up to three developments about the user's holdings, chosen in code on this
// device (utils/intelRank.js) from the clusters the server wrote twice a day
// (netlify/functions/intel-scheduled.js). Each card keeps the spec's fixed
// structure: why it was chosen, what happened (each line opens with its
// outlet), what is new, what is not yet known, the next event, the sources.
// The score is never shown · only the reason.
//
// Not here any more (spec, "מה לא"): indices, "בסגירת המסחר", "דוחות
// השבוע" and "מושג השבוע". Index prices live in the ticker tape only.
// Indirect ties (spec layer 3) come in a later sprint: no such card.
//
// Sprint 21: the company's own reports to the SEC (8-K, 6-K) are cards too,
// above news of the same kind, and one card with the news about the same
// event (utils/intelRank.js). Each card adds "מה דווח" (the checked summary
// of the report), "על החברה" (from the annual report), "בתיק שלך" (built in
// code) and the link to the document. Below the cards, in this order:
// insiders who bought or sold (Form 4), the open topics, "תמונת השוק"
// (closed by default), and the geopolitical events that touch the portfolio
// (nothing when none do).
//
// Stage 3: "נושאים פתוחים" (utils/intelTopics.js, built in code), and the
// geopolitical intelligence, moved here from the news screen with its logic
// unchanged (GeoIntelTab). On a desktop the developments are the main area
// and the topics and "נבדק ולא נבחר" the side area (VISION: a main area and
// a side area, not one stretched column).

const CARD = {
  background: UI.surface.card,
  borderRadius: T.radius.xl,
  boxShadow: UI.shadow.cardEl,
  padding: "18px 16px",
};

// Israel's calendar and clock, whatever the device's zone.
const ddmm = iso => new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", timeZone: "Asia/Jerusalem" });
// Numbers inside a Hebrew line, isolated LTR.
const Iso = ({ text }) => String(text).split(/(\d[\d/:.]*)/).map((p, i) => (i % 2 ? <LTR key={i} className="si-num">{p}</LTR> : p));
const hhmm = iso => new Date(iso).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Jerusalem" });
const REPORT_HOUR = { bmo: "לפני הפתיחה", amc: "אחרי הסגירה", dmh: "במהלך המסחר" };

function Field({ label, children, accent = false }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: accent ? UI.text.primary : UI.text.muted }}>{label}</span>
      <div style={{ fontSize: 15, lineHeight: 1.6, color: UI.text.primary }}>{children}</div>
    </div>
  );
}

// "Reuters" linked to its report · the newest report of each outlet in the
// cluster.
function Sources({ cluster, filing }) {
  const bySource = new Map();
  for (const a of [...(cluster?.articles || [])].sort((x, y) => y.datetime - x.datetime)) {
    if (!bySource.has(a.source) && /^https?:\/\//.test(a.url || "")) bySource.set(a.source, a.url);
  }
  const list = [...bySource.entries()];
  if (filing) list.unshift([`דיווח ${filing.form} ל-SEC`, filing.doc_url]);
  if (filing?.press_url) list.splice(1, 0, ["הודעה לעיתונות", filing.press_url]);
  if (!list.length) return null;
  return (
    <span style={{ fontSize: 12.5, color: UI.text.muted, lineHeight: 1.7 }}>
      {list.length === 1 ? "מקור: " : "מקורות: "}
      {list.map(([name, url], i) => (
        <span key={name}>{i ? ", " : ""}
          <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: UI.interactive, textDecoration: "none", fontWeight: 600 }}><Bidi text={name}/></a>
        </span>
      ))}
    </span>
  );
}

const tone = v => (shownDir(v) === 0 ? UI.text.primary : shownDir(v) > 0 ? UI.gain : UI.loss);
const PctTone = ({ v }) => <LTR className="si-num" style={{ fontWeight: 700, color: tone(v) }}>{fmtPct(v)}</LTR>;

// "בתיק שלך" · the holding's weight, its change on cost, and the stock's move
// since the close before the development's day (Yahoo Finance's closes).
function InYourPortfolio({ pick, portfolio, closes }) {
  const h = holdingStats(portfolio, pick.symbol);
  if (!h) return null;
  const day = dayOf(pick);
  const mv = closes ? moveSince(closes[pick.symbol]?.closes, day, h.price) : null;
  const since = pick.kind === "filing" ? "מאז הדיווח" : "מאז הידיעה הראשונה";
  return (
    <Field label="בתיק שלך">
      <span style={{ fontSize: 14 }}>
        <LTR className="si-num">{fmtPct(pick.weight, { decimals: 1, signed: false })}</LTR> מהתיק
        {h.pnlPct != null && <> · מהעלות <PctTone v={h.pnlPct}/></>}
        {mv && <> · {since} <PctTone v={mv.pct}/> <span style={{ color: UI.text.muted, fontSize: 12.5 }}>(מסגירת <LTR className="si-num">{ddmm(`${mv.baseDate}T16:00:00Z`)}</LTR> לפי <LTR>Yahoo Finance</LTR> {mv.live ? "ועד המחיר בתיק" : <>ועד המחיר האחרון ב-<LTR>Yahoo Finance</LTR></>})</span></>}
      </span>
    </Field>
  );
}

function IntelCard({ pick, n, of, next, first, about, portfolio, closes }) {
  const c = pick.cluster;
  const f = pick.filing;
  const [direct, kind, sources] = pick.reason;
  return (
    <article data-intel-card={pick.id} data-intel-kind={pick.kind} style={{ ...CARD, display: "flex", flexDirection: "column", gap: 14, border: `1px solid ${first ? UI.line.strong : UI.line.hairline}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: UI.text.muted }}><LTR className="si-num">{n}</LTR> מתוך <LTR className="si-num">{of}</LTR></span>
        {/* Neutral, not gold: gold is for AI, Pro and the main action only
            (VISION.md), and this chip is a fact about the portfolio. */}
        <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: UI.fill.subtle, color: UI.text.primary, whiteSpace: "nowrap" }}>
          <LTR style={{ fontFamily: FONT.latin }}>{pick.symbol}</LTR> · <LTR className="si-num">{fmtPct(pick.weight, { decimals: 1, signed: false })}</LTR> מהתיק
        </span>
      </div>

      <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800, lineHeight: 1.4, color: UI.text.primary }}><Bidi text={pick.title}/></h2>

      <Field label="למה נבחר עבורך" accent>
        <span style={{ fontSize: 14 }}>{direct} · סוג האירוע: {kind} · <Iso text={sources}/></span>
      </Field>

      {f && (
        <Field label="מה דווח">
          <span style={{ display: "block", fontSize: 12.5, color: UI.text.muted, marginBottom: 2 }}>
            <LTR>{f.form}</LTR> מ-<LTR className="si-num">{ddmm(`${f.filed_date}T16:00:00Z`)}</LTR>
          </span>
          {f.summary.map((l, i) => <span key={i}>{i ? " " : ""}<Bidi text={l.text}/></span>)}
        </Field>
      )}

      {c && (
        <Field label={f ? "מה קרה בחדשות" : "מה קרה"}>
          {c.what.map((l, i) => <span key={i}>{i ? " " : ""}<Bidi text={l.text}/></span>)}
        </Field>
      )}

      {pick.added && (
        <Field label="מה חדש">
          {pick.added.count === 1 ? "נוסף דיווח אחד" : <>נוספו <LTR className="si-num">{pick.added.count}</LTR> דיווחים</>} מאז שהוצג כאן ב-<LTR className="si-num">{pick.added.since}</LTR>
        </Field>
      )}
      {!pick.added && pick.shownBefore && (
        <Field label="מה חדש">
          {pick.shownBefore.title
            ? <>על אותה חברה ומאותו סוג הוצג כאן ב-<LTR className="si-num">{pick.shownBefore.date}</LTR>: <Bidi text={pick.shownBefore.title}/></>
            : <>על אותה חברה ומאותו סוג הוצג כאן ב-<LTR className="si-num">{pick.shownBefore.date}</LTR></>}
        </Field>
      )}

      {c?.unknown?.text && (
        <Field label="מה עוד לא ידוע"><Bidi text={c.unknown.text}/></Field>
      )}

      {about && (
        <Field label="על החברה">
          <Bidi text={about.text}/>
          <span style={{ display: "block", fontSize: 12.5, marginTop: 2 }}>
            <a href={about.url} target="_blank" rel="noopener noreferrer" style={{ color: UI.interactive, textDecoration: "none", fontWeight: 600 }}>
              מתוך הדוח השנתי ל-<LTR>SEC</LTR>{about.form && <> (<LTR>{about.form}</LTR>{about.year && <> · <LTR className="si-num">{about.year}</LTR></>})</>}
            </a>
          </span>
        </Field>
      )}

      <InYourPortfolio pick={pick} portfolio={portfolio} closes={closes}/>

      {next && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, background: UI.surface.sunken }}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontSize: 12, color: UI.text.muted }}>האירוע הבא</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: UI.text.primary }}>
              {next.label} של <LTR style={{ fontFamily: FONT.latin }}>{pick.symbol}</LTR> · <LTR className="si-num">{ddmm(`${next.date}T12:00:00Z`)}</LTR>
              {REPORT_HOUR[next.hour] && <span style={{ fontWeight: 400, color: UI.text.secondary }}> · {REPORT_HOUR[next.hour]}</span>}
            </span>
          </div>
          {daysUntilText(next.date) && <span style={{ fontSize: 13, fontWeight: 700, color: UI.text.primary, whiteSpace: "nowrap" }}><Iso text={daysUntilText(next.date)}/></span>}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap", borderTop: `1px solid ${UI.line.hairline}`, paddingTop: 10 }}>
        <Sources cluster={c} filing={f}/>
        {ACADEMY_ENABLED && pick.academy && isLessonShown(pick.academy.lesson) && (
          <button type="button" onClick={() => openAcademyLesson(pick.academy.lesson)}
            style={{ background: "transparent", border: "none", padding: 0, minHeight: 44, fontSize: 13, fontWeight: 700, color: UI.interactive, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 6 }}>
            {pick.academy.label}<span aria-hidden style={{ fontSize: 16, lineHeight: 1 }}>←</span> אקדמיה
          </button>
        )}
      </div>
    </article>
  );
}

// The state of the page, from what has arrived.
export function useIntel(portfolio) {
  const settled = (portfolio || []).length > 0 && !portfolio.some(p => p.quote === "pending");
  const symbols = useMemo(() => [...new Set((portfolio || []).filter(p => p.quote !== "none" && p.shares > 0).map(p => p.symbol))].sort(), [portfolio]);
  const key = symbols.join(",");
  const [data, setData] = useState(undefined);   // undefined = loading · null = failed
  useEffect(() => {
    if (!key) { setData({ clusters: [], filings: [], names: {}, seen: [], calendar: [] }); return undefined; }
    let alive = true;
    setData(undefined);
    const list = key.split(",");
    Promise.all([
      fetchIntelClusters(list),
      fetchSecFilings(list, { days: 14 }),
      fetchCompanyNames(list),
      fetchIntelSeen(),
      fetchEarningsCalendar(list, { days: 45 }).catch(() => []),
    ]).then(([clusters, filings, names, seen, calendar]) => {
      // The reports failing to load leave the news cards as they were.
      if (alive) setData(clusters === null ? null : { clusters, filings: filings || [], names, seen, calendar: (calendar || []).filter(r => r.isUserHolding) });
    });
    return () => { alive = false; };
  }, [key]);

  // What the cards add and the page's lower sections need · not waited for.
  const [extra, setExtra] = useState({ about: {}, closes: null, insiders: undefined });
  useEffect(() => {
    if (!key) { setExtra({ about: {}, closes: {}, insiders: [] }); return undefined; }
    let alive = true;
    const list = key.split(",");
    setExtra({ about: {}, closes: null, insiders: undefined });
    fetchCompanyAbout(list).then(about => { if (alive) setExtra(x => ({ ...x, about })); });
    fetchCloses(list, { range: "1mo" }).then(closes => { if (alive) setExtra(x => ({ ...x, closes })); });
    fetchInsiderTrades(list).then(insiders => { if (alive) setExtra(x => ({ ...x, insiders })); });
    return () => { alive = false; };
  }, [key]);

  // Ranked once per load, so a write to intel_seen does not reshuffle the
  // page under the reader.
  const ranked = useMemo(
    () => (data && settled ? rankFor({ clusters: data.clusters, filings: data.filings, names: namesFor(portfolio, data.names), portfolio, seen: data.seen, now: new Date() }) : null),
    // portfolio prices refresh every 30s; the ranking is held for this load
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, settled]);
  return { data, ranked, symbols, settled, extra };
}

// The name a headline would use: the portfolio's (from the quote feed) when
// it is a name and not the ticker, else the SEC's.
function namesFor(portfolio, secNames = {}) {
  const out = { ...secNames };
  for (const p of portfolio || []) if (p.name && p.name !== p.symbol) out[p.symbol] = p.name;
  return out;
}

// "נושאים פתוחים" · a development with the next dated event of its company.
function OpenTopics({ topics, onCalendar }) {
  if (!topics.length) return null;
  return (
    <section data-intel-topics style={{ ...CARD, display: "flex", flexDirection: "column", gap: 4 }}>
      <h2 style={{ ...TYPE.heading, margin: 0 }}>נושאים פתוחים</h2>
      <span style={{ fontSize: 13, color: UI.text.muted, marginBottom: 4 }}>התפתחויות מ-<LTR className="si-num">14</LTR> הימים האחרונים, והאירוע הקרוב ביומן לאותה חברה</span>
      {topics.map((t, i) => (
        <div key={t.id} style={{ display: "flex", flexDirection: "column", gap: 3, padding: "10px 0", borderTop: i ? `1px solid ${UI.line.hairline}` : "none" }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: UI.text.primary, lineHeight: 1.45 }}><Bidi text={t.title}/></span>
          <span style={{ fontSize: 13, color: UI.text.secondary }}>
            פתוח מאז <LTR className="si-num">{t.since}</LTR> · {t.event.label} של <LTR style={{ fontFamily: FONT.latin }}>{t.symbol}</LTR> ב-<LTR className="si-num">{t.event.dateText}</LTR>
          </span>
          <span style={{ fontSize: 12, color: UI.text.muted }}>
            מקור: <a href={t.source.url} target="_blank" rel="noopener noreferrer" style={{ color: UI.interactive, textDecoration: "none", fontWeight: 600 }}><LTR>{t.source.name}</LTR></a>
            {" · "}מועד הדוח: <LTR>Finnhub</LTR>
          </span>
        </div>
      ))}
      {/* "מקושר ליומן" (spec): the dates are the calendar's. */}
      {onCalendar && (
        <button type="button" onClick={onCalendar}
          style={{ alignSelf: "flex-start", background: "transparent", border: "none", padding: 0, minHeight: 44, fontSize: 13.5, fontWeight: 700, color: UI.interactive, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 6 }}>
          ליומן<span aria-hidden style={{ fontSize: 16, lineHeight: 1 }}>←</span>
        </button>
      )}
    </section>
  );
}

// `request`: { focusUrl, seq } · a geopolitical event another screen asked to
// open (the home screen's row and insight card).
export default function AIAnalysisTab({ portfolio = [], watchlist = [], portfolioReady = true, onNavigate, request = null, onRequestHandled }) {
  const isDesktop = useIsDesktop();
  const { data, ranked, symbols, settled, extra } = useIntel(portfolio);
  // Geopolitical events that touch the portfolio · the section is shown only
  // once there is one (null while they load).
  const [geoCount, setGeoCount] = useState(null);
  const [geoFocus, setGeoFocus] = useState(null);
  const geoRef = useRef(null);
  const [scrollToGeo, setScrollToGeo] = useState(false);
  useEffect(() => {
    if (!request) return;
    setGeoFocus(request.focusUrl || null);
    setScrollToGeo(true);
    onRequestHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.seq]);

  // What was shown is written once per load (intel_seen).
  const wrote = useRef(null);
  useEffect(() => {
    if (!ranked?.picked?.length) return;
    const ids = ranked.picked.map(p => p.id).join(",");
    if (wrote.current === ids) return;
    wrote.current = ids;
    markIntelSeen(seenRows(ranked.picked));
  }, [ranked]);

  const updated = useMemo(() => {
    const t = (data?.clusters || []).map(c => c.summarized_at).filter(Boolean).sort().pop();
    return t || null;
  }, [data]);
  const held = Object.keys(holdingWeights(portfolio)).length || symbols.length;
  const loading = !portfolioReady || data === undefined || (portfolio.length > 0 && !settled);

  const pickedArticles = (ranked?.picked || []).reduce((a, p) => a + (p.cluster?.article_count || 0), 0);
  const notChosen = ranked ? Math.max(0, ranked.articles - pickedArticles) : 0;
  const topics = useMemo(() => (data && ranked ? openTopics({
    clusters: data.clusters, held: holdingWeights(portfolio), calendar: data.calendar,
    skip: ranked.picked.map(p => p.cluster?.id).filter(Boolean), now: new Date(),
  }) : []), [data, ranked]); // eslint-disable-line react-hooks/exhaustive-deps

  // The section is at the foot of the page: brought into view once what is
  // above it has loaded, so it does not move after the scroll (review).
  useEffect(() => {
    if (!scrollToGeo || data === undefined) return undefined;
    const t = setTimeout(() => { geoRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); setScrollToGeo(false); }, 400);
    return () => clearTimeout(t);
  }, [scrollToGeo, data]);

  // The side area always has "תמונת השוק" · two columns on a desktop.
  const twoCols = isDesktop;
  const full = twoCols ? { gridColumn: "1 / -1" } : null;
  return (
    <div data-intel style={{
      display: "grid", gap: 18, alignItems: "start",
      gridTemplateColumns: twoCols ? "minmax(0, 1.55fr) minmax(0, 1fr)" : "minmax(0, 1fr)",
    }}>
      <header style={{ display: "flex", flexDirection: "column", gap: 6, padding: "2px 2px 0", ...full }}>
        <h1 style={{ ...TYPE.h1, margin: 0 }}>מודיעין שוק</h1>
        <span style={{ fontSize: 15, lineHeight: 1.5, color: UI.text.primary }}>מה קרה השבוע שנוגע למה שאתה מחזיק</span>
        {ranked && (
          <span data-intel-meta style={{ fontSize: 12, color: UI.text.muted }}>
            נבדקו <LTR className="si-num">{ranked.articles}</LTR> ידיעות{ranked.filings > 0 && (ranked.filings === 1 ? <> ודיווח אחד ל-<LTR>SEC</LTR></> : <> ו-<LTR className="si-num">{ranked.filings}</LTR> דיווחים ל-<LTR>SEC</LTR></>)} על {held === 1 ? "החזקה אחת שלך" : <><LTR className="si-num">{held}</LTR> ההחזקות שלך</>} · נבחרו <LTR className="si-num">{ranked.picked.length}</LTR>
            {updated && <> · עודכן <LTR className="si-num">{ddmm(updated)} · {hhmm(updated)}</LTR></>}
          </span>
        )}
      </header>

      <div style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 0 }}>
      {portfolioReady && !portfolio.length ? (
        <div style={CARD}><EmptyState icon="wallet" line="אין החזקות בתיק עדיין" sub="המודיעין נבנה מהחדשות על החברות שבתיק"/></div>
      ) : loading ? (
        <div style={CARD}><Loading><Skel w="40%" h={12} mb={12}/><Skel w="85%" h={20} mb={14}/><Skel w="95%" h={14} mb={8}/><Skel w="75%" h={14}/></Loading></div>
      ) : data === null ? (
        <div style={CARD}><EmptyState icon="news" line="לא ניתן לטעון את המודיעין כרגע" sub="נסו שוב מאוחר יותר"/></div>
      ) : ranked.picked.length === 0 ? (
        <div data-intel-quiet style={CARD}>
          {/* VISION: a state line states a finding, not an assessment ("שקט").
              The spec's "שבוע שקט בהחזקות שלך" waits for David (DECISIONS). */}
          <EmptyState icon="check" line="אין השבוע התפתחויות חדשות בהחזקות שלך"
            sub={ranked.considered
              ? "לא נמצאה השבוע התפתחות חדשה שעוברת את סף הבחירה, לפי משקל ההחזקה וסוג האירוע"
              : "לא נמצאו השבוע ידיעות על ההחזקות שלך שעברו את הסינון"}/>
        </div>
      ) : (
        ranked.picked.map((p, i) => (
          <IntelCard key={p.id} pick={p} n={i + 1} of={ranked.picked.length} first={i === 0}
            next={nextEventFor(p.symbol, data.calendar)} about={extra.about[p.symbol] || null}
            portfolio={portfolio} closes={extra.closes}/>
        ))
      )}

      {ranked && notChosen > 0 && (
        <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <h2 style={{ ...TYPE.heading, margin: 0 }}>נבדק ולא נבחר</h2>
          <div style={{ ...CARD, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 14, lineHeight: 1.5, color: UI.text.primary }}>
              {notChosen === 1
                ? "ידיעה אחת נוספת על ההחזקות שלך לא נבחרה לכאן"
                : <><LTR className="si-num" style={{ fontWeight: 700 }}>{notChosen}</LTR> ידיעות נוספות על ההחזקות שלך לא נבחרו לכאן</>}
            </span>
            {onNavigate && (
              <button type="button" onClick={() => onNavigate("news", { newsCategory: "my" })}
                style={{ alignSelf: "flex-start", background: "transparent", border: "none", padding: 0, minHeight: 44, fontSize: 14, fontWeight: 700, color: UI.interactive, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 6 }}>
                לכל החדשות על התיק<span aria-hidden style={{ fontSize: 16, lineHeight: 1 }}>←</span>
              </button>
            )}
          </div>
        </section>
      )}
      </div>

      {/* Below the cards, in this order (sprint 21): insiders, open topics,
          the market picture. On a desktop, the side area. */}
      <div style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 0 }}>
        {portfolio.length > 0 && <IntelInsiders trades={extra.insiders}/>}
        <OpenTopics topics={topics} onCalendar={onNavigate ? () => onNavigate("calendar") : null}/>
        <MarketPicture portfolio={portfolio} watchlist={watchlist} onNavigate={onNavigate}/>
      </div>

      {/* מודיעין גיאופוליטי (stage 3) · moved from the news screen, its logic
          unchanged. Sprint 21: only the events that touch the portfolio,
          and no section at all when none do. The ⓘ stays: no update is not
          evidence of no risk. */}
      <section ref={geoRef} data-intel-geo style={{ display: geoCount ? "flex" : "none", flexDirection: "column", gap: 10, scrollMarginTop: 140, ...full }}>
        {/* Wraps, so the ⓘ's note opens under the heading, not beside it. */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <h2 style={{ ...TYPE.heading, margin: 0 }}>מודיעין גיאופוליטי</h2>
          <InfoTip label="על היעדר אירועים" text="היעדר עדכון אינו מעיד על היעדר סיכון" size={14} noteStyle={{ flexBasis: "100%" }}/>
        </div>
        <GeoIntelTab portfolio={portfolio} watchlist={watchlist} focusUrl={geoFocus} onFocusHandled={() => setGeoFocus(null)}
          overlapOnly onCount={setGeoCount}/>
      </section>

      <p style={{ margin: "4px 0 0", fontSize: 11, color: UI.text.muted, textAlign: "center", lineHeight: 1.7, ...full }}>
        "מה דווח": סיכום <LTR>AI</LTR> של הדיווח ל-<LTR>SEC</LTR> ושל ההודעה לעיתונות שצורפה אליו, עם מקור לכל משפט · "מה קרה" ו"מה עוד לא ידוע": סיכום <LTR>AI</LTR> של כותרות ותקצירים מ-<LTR>Finnhub</LTR>, עם מקור לכל טענה · ייתכנו אי-דיוקים
        <br/>"על החברה": תרגום של תיאור העסק מהדוח השנתי ל-<LTR>SEC</LTR> · "בעלי עניין": דיווחי <LTR>Form 4</LTR> ב-<LTR>SEC EDGAR</LTR>
        <br/>"למה נבחר", "מה חדש", "בתיק שלך" ו"נושאים פתוחים": נבנים בקוד מנתוני התיק, מסגירות יומיות מ-<LTR>Yahoo Finance</LTR>, מהפעמים הקודמות שבהן הוצג ומהיומן · "האירוע הבא": לוח הדוחות של <LTR>Finnhub</LTR>
        <br/>מידע בלבד · אינו ייעוץ השקעות
      </p>
    </div>
  );
}

